import React from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { useModalProvider } from "../../../../../../../../Providers/ModalProvider"
import { editedLayoutGroupAtom, selectedLayoutAtom, selectedLayoutGroupAtom, uneditedLayoutGroupAtom } from "../../../../Atoms/Layout"
import { WAVEFORM_WINDOW_TIME_PRESETS } from "../../../../../../../../Managers/VisualizationManager/Viewport/Components/XAxis"
import { persystEEGAtom } from "../../../../Atoms/PersystWindow"
import { VisualizationToolbar } from "../../../React/VisualizationToolbar"
import { ConfigurePersystEEGModal } from "./ConfigurePersystEEGModal"
import { MobergDropdown, MobergDropdownSize } from "../../../../../../../../Components/MobergDropdown/MobergDropdown"
import { EEGCollapseMenu } from "../../../../../../../../Managers/VisualizationManager/WindowSettings/EEGCollapseMenuSettings"
import { PERSYST_EEG_SENSITIVITY } from "../../../../Types/EEG"
import { currentPatientFileInfoAtom } from "../../../../Atoms/PatientFile"
import { isEqual } from "lodash"
import { PersystMontageDefinition } from "../../../../Types/PersystWindow"

type PersystWindowSettingsProps = {
	windowId: string
}

export function PersystEEGWindowSettings(props: PersystWindowSettingsProps) {
	const { createModal } = useModalProvider()
	const setUneditedLayoutGroup = useSetRecoilState(uneditedLayoutGroupAtom)
	const setEditedLayoutGroup = useSetRecoilState(editedLayoutGroupAtom)
	const selectedLayoutGroup = useRecoilValue(selectedLayoutGroupAtom)
	const selectedLayout = useRecoilValue(selectedLayoutAtom)
	const { persystMontageDefinitions } = useRecoilValue(currentPatientFileInfoAtom)
	const atom = persystEEGAtom({ layoutId: selectedLayout?.id as string, windowId: props.windowId })
	const [persystConfig, setPersystConfig] = useRecoilState(atom)
	const [viewStart, viewEnd] = persystConfig.viewScale.domain()
	const viewDuration = viewEnd.getTime() - viewStart.getTime()

	if (persystConfig.montage === null && persystMontageDefinitions.length > 0) {
		setPersystConfig(previous => ({...previous, montage: persystMontageDefinitions[0]}))
	}

	function renderConfigureModal() {
		if (!selectedLayout) {
			console.error("Couldn't render the modal because a layout was not selected")
			return
		}

		setUneditedLayoutGroup(selectedLayoutGroup)
		setEditedLayoutGroup(selectedLayoutGroup)
		createModal(<ConfigurePersystEEGModal windowId={props.windowId} layoutId={selectedLayout.id} />)
	}

	const artifactReductionOptions = [
		{ label: "On", value: true },
		{ label: "Off", value: false }
	]

	const visualizationSpecificSettings = (
		<>
		<MobergDropdown 
			label={"Sensitivity"}
			selectedValue={persystConfig.sensitivityMicroVolts}
			size={MobergDropdownSize.SMALL} 
			onChange={value => setPersystConfig(previous => ({ ...previous, sensitivityMicroVolts: parseFloat(value as string) }))}
			options={Object.entries(PERSYST_EEG_SENSITIVITY).map(([label, value]) => ({ label, value }))}/>

		<MobergDropdown
			label={"Montage"}
			size={MobergDropdownSize.SMALL}
			selectedValue={persystConfig.montage}
			onChange={montage => setPersystConfig(previous => ({ ...previous, montage }))}
			options={persystMontageDefinitions.map(montage => ({ label: montage.name, value: montage }))} 
			equals={(a: PersystMontageDefinition, b: PersystMontageDefinition) => isEqual(a, b)} />

		<MobergDropdown 
			label={"AR"}
			selectedValue={persystConfig.isArtifactReductionEnabled ?? ""}
			onChange={isArtifactReductionEnabled => { setPersystConfig(previous => ({...previous, isArtifactReductionEnabled })) }}
			size={MobergDropdownSize.SMALL}
			options={artifactReductionOptions.map(preset => ({ label: preset.label, value: preset.value }))}
		/>
		
		<EEGCollapseMenu eegConfig={persystConfig} setEEGConfig={setPersystConfig} />
		
		</>
	)

	return (
		<VisualizationToolbar
			windowId={props.windowId}
			layoutId={selectedLayout?.id as string}
			atomValue={persystConfig}
			setAtom={setPersystConfig}
			atomKey={atom.key}
			renderConfigureModal={renderConfigureModal}
			viewDuration={viewDuration}
			timePresetOptions={WAVEFORM_WINDOW_TIME_PRESETS}
			visualizationSpecificSettings={visualizationSpecificSettings}
		/>
	)
}
